<script lang="ts" setup>
import { tpgIconSearch } from '@tpg/grammar-web-icons';
import { useFocus } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarQuaternaryButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import { Debounce, useSearchStore } from '@nora-monorepo/ui-shared';

const { t } = useI18n();
const MIN_SEARCH_LENGTH = 2;

const searchStore = useSearchStore();
const { lastQuery } = storeToRefs(searchStore);

const showSearch = ref(false);
const query = ref('');
const searchBar = ref();
const { focused } = useFocus(searchBar);

function openSearchBar() {
  showSearch.value = true;
  focused.value = true;
}

const debouncedSearch = new Debounce(search, 500);

function search() {
  debouncedSearch.cancelExecution();

  if (query.value === '') {
    clear(true);
  }
  else if (query.value.length >= MIN_SEARCH_LENGTH) {
    lastQuery.value = query.value;

    searchStore.search(query.value, true);
  }
}

function searchDebounced(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    clear(query.value !== '');
  }
  else if (event.key !== 'Enter') {
    debouncedSearch.execute();
  }
}

function onChange() {
  clear(query.value !== '');
}

function clear(withEmptyString: boolean) {
  searchStore.clearResults();

  if (!withEmptyString) {
    focused.value = false;
    showSearch.value = false;
  }

  query.value = '';
}

watch(focused, (value) => {
  if (!value && !query.value.length) {
    showSearch.value = false;
  }
});
</script>

<template>
  <div class="flex grow items-center">
    <GrammarQuaternaryButton
      v-if="!showSearch"
      :icon-template="tpgIconSearch"
      @click="openSearchBar"
    />
    <div
      class="left-0 max-w-0 dark:bg-dark-bg-secondary overflow-hidden relative border-0 rounded-[4px] my-auto mr-2 items-center transition-all"
      :class="[showSearch ? 'max-w-full w-full' : 'max-w-0']"
      :tabindex="showSearch ? 0 : -1"
    >
      <GrammarTextField
        ref="searchBar"
        v-model:value="query"
        :placeholder="t('common.search')"
        style="--buttonQuaternarySpacingMainRegularPadding: 0.5rem"
        :clear-button="true"
        @change="onChange"
        @keydown="searchDebounced"
      />
    </div>
  </div>
</template>
